import { useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrVdiGrundlagen, MsrVdiZeilenvorlage } from './typen';
import { ErrorState } from '../../muster';

/**
 * Eine geparste Spalte/Wert-Angabe aus dem Bearbeitungstext, z. B. aus
 * „1.1.AI=1;3.1.BH=1" die beiden Paare `{abschnitt:'1.1.', kurz:'AI', wert:1}`
 * und `{abschnitt:'3.1.', kurz:'BH', wert:1}`. Der Trenner zwischen Abschnitt
 * und Kurzform ist implizit: die Kurzform ist der abschließende Lauf aus
 * Großbuchstaben, alles davor der Abschnitt.
 */
interface EingabePaar { abschnitt: string; kurz: string; wert: number; }

function paareLesen(text: string): EingabePaar[] | null {
  const teile = text.split(';').map((t) => t.trim()).filter((t) => t.length > 0);
  if (teile.length === 0) return [];

  const ergebnis: EingabePaar[] = [];
  for (const teil of teile) {
    const m = /^(.+)=(-?\d+)$/.exec(teil);
    if (!m) return null;
    const links = /^(.*?)([A-Z]+)$/.exec(m[1].trim());
    if (!links) return null;
    ergebnis.push({ abschnitt: links[1].trim(), kurz: links[2], wert: Number(m[2]) });
  }
  return ergebnis;
}

const ohneEndpunkt = (s: string) => s.replace(/\.$/, '');

/** Eine gedruckte Zeile (alle Zeilenvorlagen mit gleichem Aggregat + Suffix). */
function ZeilenGruppe({
  aggregat, suffix, zeilen, herkunft, api, projektId, onGeaendert, spalten,
}: {
  aggregat: string; suffix: string; zeilen: MsrVdiZeilenvorlage[]; herkunft: string;
  api: ApiClient; projektId: string; onGeaendert: () => void;
  spalten: MsrVdiGrundlagen['spalten'];
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState('');
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const kurzVon = (schluessel: string) => spalten.find((s) => s.schluessel === schluessel)?.kurz ?? schluessel;

  const chips = zeilen
    .slice().sort((a, b) => a.reihenfolge - b.reihenfolge)
    .map((z) => `${kurzVon(z.spalte)} ${z.wert}`)
    .join(' · ');

  const bedingung = zeilen.find((z) => z.bedingung)?.bedingung ?? '—';
  const begruendung = zeilen.find((z) => z.begruendung)?.begruendung ?? '—';
  const aktiv = zeilen.every((z) => z.istAktiv) ? 'ja' : zeilen.some((z) => z.istAktiv) ? 'teilweise' : 'nein';

  const bearbeitenOeffnen = () => {
    setText(zeilen
      .slice().sort((a, b) => a.reihenfolge - b.reihenfolge)
      .map((z) => {
        const spalte = spalten.find((s) => s.schluessel === z.spalte);
        const abschnitt = spalte?.abschnitt ?? spalte?.gruppe ?? '';
        return `${abschnitt}${spalte?.kurz ?? z.spalte}=${z.wert}`;
      })
      .join(';'));
    setFehler(null);
    setBearbeiten(true);
  };

  const speichern = async () => {
    const paare = paareLesen(text);
    if (!paare) {
      setFehler('Nicht lesbar — Muster „1.1.AI=1;3.1.BH=1".');
      return;
    }

    const aenderungen: { zeile: MsrVdiZeilenvorlage; wert: number }[] = [];
    for (const paar of paare) {
      const spalte = spalten.find((s) => s.kurz === paar.kurz
        && ohneEndpunkt(s.abschnitt ?? s.gruppe) === ohneEndpunkt(paar.abschnitt));
      if (!spalte) {
        setFehler(`Unbekannter Spaltenschlüssel „${paar.abschnitt}${paar.kurz}".`);
        return;
      }
      const zeile = zeilen.find((z) => z.spalte === spalte.schluessel);
      if (!zeile) {
        setFehler(`Für „${aggregat}${suffix ? ` ${suffix}` : ''}" gibt es keine Zeile für Spalte `
          + `„${spalte.kurz}" — Anlegen ist hier nicht möglich.`);
        return;
      }
      if (zeile.wert !== paar.wert) aenderungen.push({ zeile, wert: paar.wert });
    }

    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      for (const { zeile, wert } of aenderungen) {
        const { id, ...rest } = zeile;
        await api.msrVdiZeilenvorlageSetzen(projektId, id, { ...rest, wert });
      }
      setBearbeiten(false);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  if (!bearbeiten) {
    return (
      <tr>
        <td style={{ fontFamily: 'monospace' }}>{aggregat}</td>
        <td>{suffix || <span className="schwach">—</span>}</td>
        <td style={{ fontSize: 12 }}>{chips || <span className="schwach">—</span>}</td>
        <td style={{ fontSize: 11 }}>{bedingung}</td>
        <td>{herkunft}</td>
        <td style={{ fontSize: 11 }}>{begruendung}</td>
        <td>{aktiv}</td>
        <td>
          <button type="button" className="knopf" onClick={bearbeitenOeffnen}>Bearbeiten</button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={8}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: 'monospace' }}>{aggregat} {suffix}</span>
          <input type="text" value={text} style={{ flex: '1 1 260px' }}
            aria-label={`Spaltenwerte ${aggregat}${suffix ? ` ${suffix}` : ''}`}
            placeholder="1.1.AI=1;3.1.BH=1"
            onChange={(e) => setText(e.target.value)} />
          <button type="button" className="knopf betont" disabled={speichertLaeuft}
            onClick={speichern}>Speichern</button>
          <button type="button" className="knopf" onClick={() => setBearbeiten(false)}>
            Abbrechen
          </button>
        </div>
        {/* TM-8c: Fehler als ErrorState; die Tabelle selbst bleibt im Vorgabeformat (F-7, Ausnahme S1.12). */}
        {fehler && <ErrorState klein titel="Nicht gespeichert." meldung={fehler} />}
      </td>
    </tr>
  );
}

/**
 * Unterkarte „VDI 3814 Blatt 4.3" im Reiter Grundlagen — Spaltenkatalog
 * (Abschnitt 13.1), Aggregatkatalog (Abschnitt 13.3) und Zeilenvorlagen je
 * Aggregatkürzel (Abschnitt 13.4). Die Zeilen der GA-Funktionsliste werden
 * zur Laufzeit aus den Zeilenvorlagen gebildet, nicht gespeichert — jede
 * Änderung hier wirkt deshalb sofort auf die nächste Anzeige oder den
 * nächsten Export, ohne einen Ableitungslauf zu brauchen.
 *
 * `Herkunft` ist im Vertrag keine Eigenschaft der einzelnen Zeilenvorlage,
 * nur des Aggregatkürzels (`aggregatkatalog.quelleVorlage`) — die Spalte
 * hier ist deshalb aus dem Aggregat abgeleitet, nicht aus der Zeile selbst.
 */
export function VdiGrundlagen({ api, projektId, grundlagen, onGeaendert }: {
  api: ApiClient; projektId: string; grundlagen: MsrVdiGrundlagen; onGeaendert: () => void;
}) {
  const gruppen = new Map<string, MsrVdiZeilenvorlage[]>();
  for (const z of grundlagen.zeilenvorlagen) {
    const schluessel = `${z.aggregat}|${z.suffix}`;
    const liste = gruppen.get(schluessel) ?? [];
    liste.push(z);
    gruppen.set(schluessel, liste);
  }

  const herkunftVon = (aggregat: string) => {
    const eintrag = grundlagen.aggregatkatalog.find((k) => k.kuerzel === aggregat);
    return eintrag?.quelleVorlage ? 'Vorlage' : 'Festlegung';
  };

  return (
    <div className="karte">
      <h3 style={{ marginTop: 0 }}>VDI 3814 Blatt 4.3</h3>
      <div className="hinweis" style={{ fontSize: 12, marginBottom: 12 }}>
        Änderungen wirken sofort auf die Liste (zur Laufzeit gebildet).
      </div>

      <h4>Spaltenkatalog</h4>
      <div className="tabellenrahmen" style={{ marginBottom: 16 }}>
        <table className="zuordnung">
          <thead>
            <tr><th>Buchstabe</th><th>Abschnitt</th><th>Titel</th><th>Kurz</th></tr>
          </thead>
          <tbody>
            {grundlagen.spalten.map((s) => (
              <tr key={s.schluessel}>
                <td style={{ fontFamily: 'monospace' }}>{s.buchstabe}</td>
                <td>{s.abschnitt ?? s.gruppe}</td>
                <td>{s.titel}</td>
                <td style={{ fontFamily: 'monospace' }}>{s.kurz}</td>
              </tr>
            ))}
            {grundlagen.spalten.length === 0 && (
              <tr><td colSpan={4} className="schwach">Kein Spaltenkatalog geladen.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h4>Aggregatkatalog</h4>
      <div className="tabellenrahmen" style={{ marginBottom: 16 }}>
        <table className="zuordnung">
          <thead>
            <tr><th>Kürzel</th><th>Bezeichnung</th><th>Gewerke</th><th>aus Vorlage</th></tr>
          </thead>
          <tbody>
            {grundlagen.aggregatkatalog.map((k) => (
              <tr key={k.kuerzel}>
                <td style={{ fontFamily: 'monospace' }}>{k.kuerzel}</td>
                <td>{k.bezeichnung}</td>
                <td style={{ fontSize: 12 }}>{k.gewerke.join(', ')}</td>
                <td>{k.quelleVorlage ? 'ja' : 'nein'}</td>
              </tr>
            ))}
            {grundlagen.aggregatkatalog.length === 0 && (
              <tr><td colSpan={4} className="schwach">Kein Aggregatkatalog geladen.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h4>Zeilenvorlagen je Aggregatkürzel</h4>
      <div className="tabellenrahmen">
        <table className="zuordnung">
          <thead>
            <tr>
              <th>Aggregat</th><th>Suffix</th><th>Spaltenwerte</th><th>Bedingung</th>
              <th>Herkunft</th><th>Begründung</th><th>aktiv</th><th></th>
            </tr>
          </thead>
          <tbody>
            {Array.from(gruppen.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([schluessel, zeilen]) => {
                const [aggregat, suffix] = schluessel.split('|');
                return (
                  <ZeilenGruppe key={schluessel} aggregat={aggregat} suffix={suffix} zeilen={zeilen}
                    herkunft={herkunftVon(aggregat)} api={api} projektId={projektId}
                    onGeaendert={onGeaendert} spalten={grundlagen.spalten} />
                );
              })}
            {gruppen.size === 0 && (
              <tr><td colSpan={8} className="schwach">Keine Zeilenvorlagen geladen.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
